import React from 'react';

import {useKeyInfoQueryContext} from '../MultiRunWorkspace/KeyInfoQueryContext';
import {type RunsLinePlotConfig} from './types';
import {getTruncatedSelectedMetrics} from './utils';

type MetricsConfig = {
  metricRegex?: string;
  metrics?: string[];
  useMetricRegex?: boolean;
};

// If we're using a regex to determine y-axis metrics,
// update the chart when new historyKeys come in that match the regex.
// Uses isUndoable: false to avoid triggering "unsaved changes" prompt
// for automatic metric expansion.
export const useRegexMetrics = (
  config: MetricsConfig,
  updateConfig: (
    arg: Partial<RunsLinePlotConfig>,
    options?: {isUndoable?: boolean}
  ) => void,
  // NGW resolves regex → metrics server-side (useRegexMetricsSync); disable this
  // history-key path when that's active.
  {enabled = true}: {enabled?: boolean} = {}
) => {
  const {
    keyInfoQuery: {historyKeyInfo, loading},
  } = useKeyInfoQueryContext();

  React.useEffect(() => {
    if (
      !enabled ||
      !config.useMetricRegex ||
      !config.metricRegex ||
      historyKeyInfo == null ||
      loading
    ) {
      return;
    }

    const regexMatchMetrics = getTruncatedSelectedMetrics({
      metricRegex: config.metricRegex,
      keyInfo: historyKeyInfo,
      sortedKeys: historyKeyInfo.sortedKeys,
    });
    const arraysDifferentLength =
      config.metrics?.length !== regexMatchMetrics.length;
    const arraysMatch = (config.metrics ?? []).every(metric =>
      regexMatchMetrics.includes(metric)
    );
    if (arraysDifferentLength || !arraysMatch) {
      // Use isUndoable flag to avoid triggering unsaved changes prompt
      updateConfig({metrics: regexMatchMetrics}, {isUndoable: false});
    }
  }, [
    enabled,
    config.metricRegex,
    config.metrics,
    config.useMetricRegex,
    historyKeyInfo,
    updateConfig,
    loading,
  ]);
};
